/**
 * Historique des défis (espace compétition)
 *
 * Filtres : mes défis (mon équipe) / défis où j'ai joué / équipe donnée /
 * équipe A vs équipe B / type (championnat, libre, entraînement, coupe).
 */

import { useState, useEffect, useCallback } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import TeamPicker from '../../components/competitions/TeamPicker';
import { getMatchHistory, getMyTeam, type MatchItem, type MyTeam } from '../../lib/teamsApi';
import type { Session } from '../../types/app';
import { BACKEND_URL } from '../../config';

type Scope = 'mine' | 'player' | 'team' | 'versus';

const KIND_LABELS: Record<string, string> = {
  '': 'Tous types',
  championnat: 'Championnat',
  libre: 'Match libre',
  entrainement: 'Entraînement',
  coupe: 'Coupe',
};

const ETAT_LABELS: Record<string, string> = {
  proposition: 'Proposé',
  ok: 'Accepté',
  encours: 'En cours',
  fini: 'Terminé',
  refus: 'Refusé',
  annule: 'Annulé',
};

function fmtDateTime(v: string | null): string {
  if (!v) return '-';
  try {
    const d = new Date(v);
    return d.toLocaleDateString('fr-FR') + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return v;
  }
}

/** -1 : équipe1 gagne, 1 : équipe2 gagne, 0 : nul, null : pas encore de score */
function matchWinner(m: MatchItem): -1 | 0 | 1 | null {
  if (m.pm1 == null || m.pm2 == null) return null;
  if (m.pm1 > m.pm2) return -1;
  if (m.pm2 > m.pm1) return 1;
  return 0;
}

const WINNER_COLOR = 'var(--success, #2e9e5b)';

interface MatchHistoryPageProps {
  session: Session;
}

export default function MatchHistoryPage({ session }: MatchHistoryPageProps) {
  const { openTeamProfile } = useProfile();
  const [scope, setScope] = useState<Scope>('mine');
  const [teamA, setTeamA] = useState<{ id: number; nom: string } | null>(null);
  const [teamB, setTeamB] = useState<{ id: number; nom: string } | null>(null);
  const [kind, setKind] = useState('');
  const [etat, setEtat] = useState('');
  const [myTeam, setMyTeam] = useState<MyTeam | null>(null);
  const [items, setItems] = useState<MatchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session.token) return;
    getMyTeam(session.token).then((d) => setMyTeam(d.team)).catch(() => {});
  }, [session.token]);

  const runSearch = useCallback(() => {
    const params: Parameters<typeof getMatchHistory>[0] = { limit: 100, token: session.token };
    if (kind) params.kind = kind;
    if (etat) params.etat = etat;

    if (scope === 'mine') {
      if (!myTeam) { setItems([]); return; }
      params.team_id = myTeam.id;
    } else if (scope === 'player') {
      params.player = session.pseudo;
    } else if (scope === 'team') {
      if (!teamA) { setItems([]); return; }
      params.team_id = teamA.id;
    } else if (scope === 'versus') {
      if (!teamA || !teamB) { setItems([]); return; }
      params.team_id = teamA.id;
      params.opponent_team_id = teamB.id;
    }

    setLoading(true);
    setError(null);
    getMatchHistory(params)
      .then((d) => setItems(d.items))
      .catch(() => setError('Impossible de charger l\'historique'))
      .finally(() => setLoading(false));
  }, [scope, teamA, teamB, kind, etat, myTeam, session.token, session.pseudo]);

  useEffect(() => { runSearch(); }, [runSearch]);

  return (
    <div className="compet-page">
      <div className="compet-page-header" style={{ flexWrap: 'wrap' }}>
        <select value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
          <option value="mine">Mes défis{myTeam ? ` (${myTeam.nom})` : ''}</option>
          <option value="player">Défis où j'ai joué</option>
          <option value="team">Défis d'une équipe</option>
          <option value="versus">Équipe A vs équipe B</option>
        </select>
        {(scope === 'team' || scope === 'versus') && (
          <TeamPicker value={teamA} onChange={setTeamA} placeholder="Équipe A…" />
        )}
        {scope === 'versus' && (
          <TeamPicker value={teamB} onChange={setTeamB} placeholder="Équipe B…" excludeTeamId={teamA?.id} />
        )}
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(KIND_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={etat} onChange={(e) => setEtat(e.target.value)}>
          <option value="">Tous états</option>
          <option value="fini">Terminés</option>
          <option value="ok">Acceptés / à venir</option>
          <option value="proposition">En attente</option>
        </select>
      </div>
      <div className="compet-content">
        {loading && <div className="compet-loading">Chargement…</div>}
        {error && <div className="compet-error">{error}</div>}
        {!loading && !error && scope === 'mine' && !myTeam && (
          <div className="compet-empty">Vous ne faites partie d'aucune équipe.</div>
        )}
        {!loading && !error && (scope === 'team' || scope === 'versus') && !teamA && (
          <div className="compet-empty">Choisissez une équipe.</div>
        )}
        {!loading && !error && scope === 'versus' && teamA && !teamB && (
          <div className="compet-empty">Choisissez la seconde équipe.</div>
        )}
        {!loading && !error && items.length === 0 && (
          (scope !== 'mine' || myTeam) && (scope !== 'team' && scope !== 'versus' || teamA) && (scope !== 'versus' || teamB)
        ) && (
          <div className="compet-empty">Aucun défi ne correspond à ces critères</div>
        )}
        {!loading && !error && items.length > 0 && (
          <table className="compet-standings-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Équipe 1</th>
                <th>Score</th>
                <th>Équipe 2</th>
                <th>Type</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const winner = matchWinner(m);
                return (
                  <tr key={m.id}>
                    <td>{fmtDateTime(m.rdv || m.quand)}</td>
                    <td>
                      <button
                        className="tc-team-block-name"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0,
                          color: winner === -1 ? WINNER_COLOR : 'inherit',
                          fontWeight: winner === -1 ? 700 : 400,
                        }}
                        onClick={() => openTeamProfile(m.equipe1)}
                      >
                        <PlayerAvatar
                          pseudo={m.equipe1_nom || `Équipe #${m.equipe1}`}
                          size={22}
                          photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(m.equipe1_nom || String(m.equipe1))}?size=44`}
                        />
                        {m.equipe1_nom || `Équipe #${m.equipe1}`}
                        {winner === -1 && ' 🏆'}
                      </button>
                    </td>
                    <td className="num-col">
                      {m.pm1 != null && m.pm2 != null ? (
                        <span>
                          <span style={{ fontWeight: winner === -1 ? 700 : 400, color: winner === -1 ? WINNER_COLOR : 'inherit' }}>{m.pm1}</span>
                          {' - '}
                          <span style={{ fontWeight: winner === 1 ? 700 : 400, color: winner === 1 ? WINNER_COLOR : 'inherit' }}>{m.pm2}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td>
                      {m.equipe2 === 2 ? (
                        <span className="tc-team-block-name">Entraînement</span>
                      ) : (
                        <button
                          className="tc-team-block-name"
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0,
                            color: winner === 1 ? WINNER_COLOR : 'inherit',
                            fontWeight: winner === 1 ? 700 : 400,
                          }}
                          onClick={() => openTeamProfile(m.equipe2)}
                        >
                          <PlayerAvatar
                            pseudo={m.equipe2_nom || `Équipe #${m.equipe2}`}
                            size={22}
                            photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(m.equipe2_nom || String(m.equipe2))}?size=44`}
                          />
                          {m.equipe2_nom || `Équipe #${m.equipe2}`}
                          {winner === 1 && ' 🏆'}
                        </button>
                      )}
                    </td>
                    <td>{m.idcompetition === 1 ? 'Championnat' : m.equipe2 === 2 ? 'Entraînement' : m.idcompetition ? 'Coupe' : 'Libre'}</td>
                    <td>{ETAT_LABELS[m.etat] || m.etat}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
